import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Croix } from './Icones';

/**
 * Coordonnées affichées par le panneau « Contact ».
 *
 * Elles sont déjà publiques — le site actuel les affiche — mais elles vivent ici,
 * groupées et nommées, plutôt que dispersées dans le rendu : c'est le genre
 * d'information qui change (une agence, un numéro) et qu'on doit pouvoir corriger
 * sans relire un composant.
 */
const CONTACT = {
  presentation: '2D Artist & Motion Designer based in Marseille, France',
  agence: { intitule: 'Représenté par Julia Descourtis', email: 'julia@jdmotionagent.com' },
  direct: { email: 'dorian.pons123@gmail.com', telephone: '06.25.02.41.09' },
};

const INSTAGRAM = 'https://www.instagram.com/jeronimus_p/';
const BEHANCE = 'https://www.behance.net/jejeroroni0f84';

/**
 * Icône Instagram, en CONTOUR et non pleine.
 *
 * Le site d'origine utilise le glyphe Font Awesome « instagram » : un carré
 * arrondi tracé, un cercle tracé, un point. J'avais mis une version pleine, qui
 * fait une tache sombre là où l'original a un dessin fin — avec « Contact » à
 * côté en gris clair, le déséquilibre se voyait.
 *
 * En SVG inline plutôt qu'en police : l'original chargeait Font Awesome en entier,
 * une requête bloquante et plusieurs dizaines de kilo-octets pour un pictogramme.
 */
function IconeInstagram() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="2.6" y="2.6" width="18.8" height="18.8" rx="5.2" />
      <circle cx="12" cy="12" r="4.1" />
      <circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function EnTete({ rubriques }: { rubriques: React.ReactNode }) {
  const [contactOuvert, setContactOuvert] = useState(false);
  const [compact, setCompact] = useState(false);

  const repereReplier = useRef<HTMLDivElement>(null);
  const repereDeplier = useRef<HTMLDivElement>(null);

  /**
   * L'en-tête se replie quand on descend : la signature rétrécit jusqu'à la taille
   * de la barre et reste visible, au lieu de disparaître en haut de page.
   *
   * DEUX REPÈRES FIXES, ET NON LA POSITION DE DÉFILEMENT.
   *
   * Comparer `window.scrollY` à un seuil crée une boucle : replier l'en-tête réduit
   * sa hauteur d'environ 300 px, donc le document raccourcit, donc la position de
   * défilement se décale, donc le seuil est repassé — dans l'autre sens. La mesure
   * est perturbée par ce qu'elle déclenche.
   *
   * Un repère unique règle cette boucle mais pas tout : l'état bascule exactement
   * au passage du point, si bien qu'en défilant lentement autour de cette hauteur
   * l'en-tête se replie et se déplie à chaque cran de molette.
   *
   * D'où deux repères, à deux hauteurs. On replie quand le repère BAS sort de
   * l'écran, on ne déplie que lorsque le repère HAUT réapparaît. Entre les deux,
   * rien ne bouge. Tous deux sont placés en absolu : ce sont des points du
   * DOCUMENT, que replier l'en-tête ne déplace pas.
   */
  useEffect(() => {
    const bas = repereReplier.current;
    const haut = repereDeplier.current;
    if (!bas || !haut) return;

    const observateur = new IntersectionObserver(
      (entrees) => {
        for (const entree of entrees) {
          if (entree.target === bas && !entree.isIntersecting) setCompact(true);
          if (entree.target === haut && entree.isIntersecting) setCompact(false);
        }
      },
      { threshold: 0 },
    );

    observateur.observe(bas);
    observateur.observe(haut);
    return () => observateur.disconnect();
  }, []);

  useEffect(() => {
    if (!contactOuvert) return;
    const surTouche = (e: KeyboardEvent) => e.key === 'Escape' && setContactOuvert(false);
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [contactOuvert]);

  return (
    <>
      {/* Points fixes du document : voir l'explication ci-dessus. */}
      <div ref={repereDeplier} className="sentinelle-entete sentinelle-deplier" aria-hidden="true" />
      <div ref={repereReplier} className="sentinelle-entete sentinelle-replier" aria-hidden="true" />

      {/* Réserve dans le flux la place de l'en-tête, qui est en position fixe :
          sans lui, le contenu passerait dessous. Sa hauteur ne change jamais,
          c'est ce qui empêche le défilement de se rétroalimenter. */}
      {/* Réserve dans le flux la place de l'en-tête, qui est en position fixe.
          Sa hauteur est constante (variable CSS `--entete-hauteur`) : c'est ce qui
          empêche le défilement de se rétroalimenter. */}
      <div className="espaceur-entete" aria-hidden="true" />

      <header className={compact ? 'entete compacte' : 'entete'}>
        <nav className="liens-externes">
          <button className="lien-contact" onClick={() => setContactOuvert(true)}>
            Contact
          </button>
          <a href={INSTAGRAM} target="_blank" rel="noreferrer noopener" aria-label="Instagram">
            <IconeInstagram />
          </a>
          {/*
            Behance s'écrit « Bē » en toutes lettres sur le site d'origine, en gras
            et en noir — ce n'est pas un pictogramme. Le rendre en icône changeait
            l'équilibre de la barre.
          */}
          <a
            href={BEHANCE}
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Behance"
            className="lien-behance"
          >
            Bē
          </a>
        </nav>

        <Link className="lien-signature" to="/">
          <img className="signature" src="/signature.png" alt="Jeronimus" width={900} height={332} />
        </Link>

        {rubriques}

        {contactOuvert && (
          <div
            className="panneau-contact"
            role="dialog"
            aria-modal="true"
            aria-label="Contact"
            onClick={() => setContactOuvert(false)}
          >
            {/* Un clic DANS le panneau ne doit pas le fermer : on ne veut pas
                interrompre quelqu'un qui sélectionne une adresse pour la copier. */}
            <div className="panneau-contact-contenu" onClick={(e) => e.stopPropagation()}>
              <button
                className="fermer"
                onClick={() => setContactOuvert(false)}
                aria-label="Fermer"
              >
                <Croix />
              </button>
              <p className="presentation">{CONTACT.presentation}</p>
              <p>
                <strong>{CONTACT.agence.intitule}</strong>
                <br />
                <a href={`mailto:${CONTACT.agence.email}`}>{CONTACT.agence.email}</a>
              </p>
              <p>
                <strong>Contact</strong>
                <br />
                <a href={`mailto:${CONTACT.direct.email}`}>{CONTACT.direct.email}</a>
                <br />
                <a href={`tel:${CONTACT.direct.telephone.replace(/\./g, '')}`}>
                  {CONTACT.direct.telephone}
                </a>
              </p>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
